import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2, UserRound, ChevronUp, ChevronDown, Phone, Mail } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { imgUrl } from "@/pages/admin/PropertiesTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamTab() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("rk_admin_token")}` });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/team`, { headers: headers() });
      setItems(data.map((m, i) => ({ ...m, order: m.order || i + 1 })));
    } catch {
      toast.error("Could not load team members.");
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePublish = async (m) => {
    setBusy(m.id);
    try {
      await axios.put(`${API}/admin/team/${m.id}`, { ...m, published: !m.published }, { headers: headers() });
      setItems((list) => list.map((x) => (x.id === m.id ? { ...x, published: !m.published } : x)));
      toast.success(m.published ? "Unpublished — hidden from website." : "Published — live on website.");
    } catch {
      toast.error("Update failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.name}" permanently? This cannot be undone.`)) return;
    setBusy(m.id);
    try {
      await axios.delete(`${API}/admin/team/${m.id}`, { headers: headers() });
      setItems((list) => list.filter((x) => x.id !== m.id));
      toast.success("Team member deleted.");
    } catch {
      toast.error("Delete failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[i];
    const b = items[j];
    setBusy(a.id);
    try {
      await Promise.all([
        axios.put(`${API}/admin/team/${a.id}`, { ...a, order: j + 1 }, { headers: headers() }),
        axios.put(`${API}/admin/team/${b.id}`, { ...b, order: i + 1 }, { headers: headers() }),
      ]);
      const next = [...items];
      [next[i], next[j]] = [{ ...b, order: i + 1 }, { ...a, order: j + 1 }];
      setItems(next);
    } catch {
      toast.error("Reorder failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  if (items === null)
    return <div className="py-16 flex justify-center text-ink-soft"><Loader2 className="animate-spin" size={26} /></div>;

  return (
    <div data-testid="team-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-navy">Management Team</h2>
          <p className="mt-1 text-sm text-ink-soft">
            {items.length} {items.length === 1 ? "member" : "members"} · Published members appear on the homepage in this order.
          </p>
        </div>
        <Link
          to="/admin/team/new"
          data-testid="add-member-button"
          className="bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-5 py-3 text-[14px] font-semibold inline-flex items-center gap-2 shadow-md shadow-gold/25"
        >
          <Plus size={16} /> Add Member
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="mt-8 bg-white rounded-xl border border-line p-14 text-center" data-testid="team-empty-state">
          <UserRound size={30} className="mx-auto text-gold" />
          <p className="mt-4 font-display text-xl text-navy">No team members yet</p>
          <p className="mt-1.5 text-sm text-ink-soft">Add your first team member — publish to show them on the homepage.</p>
          <Link to="/admin/team/new" className="mt-6 inline-flex items-center gap-2 bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-6 py-3 text-[14px] font-semibold">
            <Plus size={15} /> Add Member
          </Link>
        </div>
      ) : (
        <div className="mt-8 space-y-3" data-testid="admin-team-list">
          {items.map((m, i) => (
            <article key={m.id} className="bg-white rounded-xl border border-line p-4 sm:p-5 flex items-center gap-4 hover:border-gold-soft transition-colors" data-testid={`admin-team-${m.id}`}>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => move(i, -1)} disabled={i === 0 || busy === m.id} className="p-1 text-ink-soft hover:text-navy disabled:opacity-25 transition-colors" aria-label="Move up" data-testid={`admin-team-up-${m.id}`}>
                  <ChevronUp size={17} />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1 || busy === m.id} className="p-1 text-ink-soft hover:text-navy disabled:opacity-25 transition-colors" aria-label="Move down" data-testid={`admin-team-down-${m.id}`}>
                  <ChevronDown size={17} />
                </button>
              </div>
              <div className="h-16 w-16 rounded-lg overflow-hidden bg-warm border border-line shrink-0">
                {m.photo ? (
                  <img src={imgUrl(m.photo)} alt={m.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display text-xl font-semibold text-gold/60">{m.name.charAt(0)}</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-display text-base sm:text-lg font-semibold text-navy leading-snug">{m.name}</h3>
                  <span className={`text-[10px] font-bold uppercase tracking-[0.1em] rounded px-2 py-0.5 border ${m.published ? "bg-navy text-white border-navy" : "bg-line/50 text-ink-soft border-line"}`} data-testid={`admin-team-status-${m.id}`}>
                    {m.published ? "Published" : "Draft"}
                  </span>
                </div>
                {m.designation && <p className="mt-0.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-gold">{m.designation}</p>}
                <p className="mt-1 text-[12px] text-ink-soft flex flex-wrap gap-x-4">
                  {m.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {m.phone}</span>}
                  {m.email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {m.email}</span>}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Link to={`/admin/team/${m.id}/edit`} data-testid={`admin-team-edit-${m.id}`}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-navy border border-line hover:border-gold rounded-md px-3 py-2 transition-colors">
                  <Pencil size={12} /> Edit
                </Link>
                <button onClick={() => togglePublish(m)} disabled={busy === m.id} data-testid={`admin-team-publish-${m.id}`}
                  className={`inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-md px-3 py-2 border transition-colors disabled:opacity-50 ${
                    m.published ? "text-ink-soft border-line hover:border-navy" : "text-navy bg-gold/20 border-gold/50 hover:bg-gold/35"
                  }`}>
                  {m.published ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
                </button>
                <button onClick={() => remove(m)} disabled={busy === m.id} data-testid={`admin-team-delete-${m.id}`}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-red-700 border border-red-200 hover:bg-red-50 rounded-md px-3 py-2 transition-colors disabled:opacity-50">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
